import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE_URL } from "../config/api";

const VISITOR_KEY = "masterSolVisitorId";

const getVisitorId = () => {
  let visitorId = localStorage.getItem(VISITOR_KEY);

  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 10)}`;

    localStorage.setItem(VISITOR_KEY, visitorId);
  }

  return visitorId;
};

const ProductDetails = () => {
  const { lang } = useLanguage();
  const { brand, model } = useParams();

  const [products, setProducts] = useState([]);
  const [mainProduct, setMainProduct] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    surname: "",
    mobile: "",
    address: "",
  });

  const colorMap = {
    Zeze: "#000000",
    Bardhe: "#ffffff",
    Gri: "#9ca3af",
    Blu: "#2563eb",
    Jeshile: "#16a34a",
    Kuqe: "#dc2626",
    Verdhe: "#eab308",
  };

  // ================= TRANSLATIONS =================
  const t = {
    sq: {
      memory: "Memorje",
      battery: "Bateri",
      color: "Ngjyra",
      condition: "Gjendja",
      inStock: "Në stok",
      outStock: "S’ka stok",
      whatsapp: "WhatsApp",
      reserve: "Rezervo",
      reserveTitle: "Rezervo Produktin",
      confirm: "Konfirmo",
      close: "Mbyll",
      loading: "Duke dërguar...",
      error: "Plotëso emrin dhe numrin e telefonit!",
      success: "✔ Rezervimi u dërgua me sukses!",
      fail: "❌ Gabim gjatë rezervimit",
      noDesc: "Pa përshkrim",
      name: "Emri",
      surname: "Mbiemri",
      phone: "Telefoni",
      address: "Adresa",
    },
    en: {
      memory: "Memory",
      battery: "Battery",
      color: "Color",
      condition: "Condition",
      inStock: "In stock",
      outStock: "Out of stock",
      whatsapp: "WhatsApp",
      reserve: "Reserve",
      reserveTitle: "Reserve Product",
      confirm: "Confirm",
      close: "Close",
      loading: "Sending...",
      error: "Fill name and phone number!",
      success: "✔ Reservation sent successfully!",
      fail: "❌ Reservation error",
      noDesc: "No description",
      name: "Name",
      surname: "Surname",
      phone: "Phone",
      address: "Address",
    },
  };

  const text = t[lang] || t.sq;

  // ================= FETCH =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/products/group/${brand}/${model}`,
        );
        const data = await res.json();

        setProducts(data || []);
        setMainProduct(data?.[0] || null);
        setSelectedId(data?.[0]?.id || null);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [brand, model]);

  // ================= PRODUCT CLICK TRACKING =================
  useEffect(() => {
    if (!mainProduct?.id) return;

    const trackProductClick = async () => {
      try {
        const visitorId = getVisitorId();

        await fetch(
          `${API_BASE_URL}/api/product-clicks/track`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              visitor_id: visitorId,
              product_id: mainProduct.id,
            }),
          }
        );
      } catch (error) {
        console.error(
          "Product click tracking error:",
          error
        );
      }
    };

    trackProductClick();
  }, [mainProduct?.id]);

  const selectProduct = (p) => {
    setMainProduct(p);
    setSelectedId(p.id);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ================= WHATSAPP =================
  const handleWhatsApp = () => {
    if (!mainProduct) return;

    const phone = "355688188188";

    const msg =
      lang === "en"
        ? `Hello 👋

I am interested in this product:

📱 Name: ${mainProduct.name}
🏷 Brand: ${mainProduct.brand}
📌 Model: ${mainProduct.model}
💰 Price: €${mainProduct.price}
📦 Status: ${mainProduct.stock > 0 ? "In stock" : "Out of stock"}

Thank you 🙏`
        : `Përshëndetje 👋

Jam i interesuar për këtë produkt:

📱 Emri: ${mainProduct.name}
🏷 Marka: ${mainProduct.brand}
📌 Modeli: ${mainProduct.model}
💰 Çmimi: €${mainProduct.price}
📦 Gjendja: ${mainProduct.stock > 0 ? "Në stok" : "S’ka stok"}

Faleminderit 🙏`;

    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`,
      "_blank",
    );
  };

  // ================= RESERVE =================
  const handleReserve = async () => {
    if (!mainProduct) return;

    if (!form.name || !form.mobile) {
      alert(text.error);
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/orders/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          surname: form.surname,
          mobile: form.mobile,
          address: form.address,
          email: null,
          product: mainProduct,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      alert(text.success);

      setForm({
        name: "",
        surname: "",
        mobile: "",
        address: "",
      });

      setMode(null);
    } catch (err) {
      console.error(err);
      alert(text.fail);
    } finally {
      setLoading(false);
    }
  };

  const colorVariants = [
  ...new Map(
    products
      .filter((p) => p.color)
      .map((p) => [p.color, p])
  ).values(),
];

const conditionVariants = [
  ...new Map(
    products
      .filter((p) => p.product_condition)
      .map((p) => [p.product_condition, p])
  ).values(),
];



  if (!mainProduct) return <div className="p-10 text-center">Loading...</div>;



 return (
  <main className="min-h-screen bg-slate-50 px-3 py-6 sm:px-5 lg:py-10">
    <div className="mx-auto w-full max-w-[1450px]">
      <div className="grid items-stretch gap-6 lg:grid-cols-[1.02fr_1fr]">
        {/* =====================================================
            LEFT — PRODUCT IMAGE, COLORS AND CONDITION
        ====================================================== */}
        <motion.section
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="
            overflow-hidden
            rounded-[26px]
            border border-slate-200
            bg-white
            shadow-[0_18px_50px_rgba(15,23,42,0.08)]
          "
        >
          {/* MAIN IMAGE */}
          <div
            className="
              relative
              flex
              min-h-[430px]
              items-center
              justify-center
              overflow-hidden
              bg-gradient-to-br
              from-slate-50
              via-blue-50/50
              to-indigo-50
              p-8
              sm:min-h-[500px]
              lg:min-h-[520px]
            "
          >
            <div
              className="
                absolute
                left-1/2
                top-[76%]
                h-16
                w-[64%]
                -translate-x-1/2
                rounded-[50%]
                bg-slate-300/30
                blur-xl
              "
            />

            <div
              className="
                absolute
                bottom-8
                left-1/2
                h-16
                w-[55%]
                -translate-x-1/2
                rounded-[50%]
                border border-slate-200
                bg-white
                shadow-[0_18px_30px_rgba(15,23,42,0.10)]
              "
            />

            <img
              src={`${API_BASE_URL}/uploads/${mainProduct.images}`}
              alt={mainProduct.name || "Produkt"}
              className="
                relative
                z-10
                h-[330px]
                w-full
                object-contain
                drop-shadow-[0_22px_20px_rgba(15,23,42,0.18)]
                transition
                duration-500
                hover:scale-105
                sm:h-[390px]
              "
            />
          </div>

          {/* VARIANT CONTROLS */}
          <div className="border-t border-slate-100 p-5 sm:p-6">
            <h3 className="text-sm font-extrabold text-slate-900">
              Zgjidh ngjyrën dhe gjendjen
            </h3>

            {/* COLORS */}
            <div className="mt-4">
              <span
                className="
                  mb-3
                  block
                  text-[11px]
                  font-extrabold
                  uppercase
                  tracking-[0.12em]
                  text-slate-400
                "
              >
                {text.color}
              </span>

              <div className="flex flex-wrap gap-3">
                {colorVariants.map((product) => {
                  const isSelected = selectedId === product.id;

                  return (
                    <button
                      type="button"
                      key={`color-${product.id}`}
                      onClick={() => selectProduct(product)}
                      className={`
                        min-w-[86px]
                        rounded-xl
                        border
                        px-3
                        py-3
                        text-center
                        transition
                        duration-200

                        ${
                          isSelected
                            ? `
                              border-violet-600
                              bg-violet-50
                              shadow-[0_0_0_3px_rgba(124,58,237,0.10)]
                            `
                            : `
                              border-slate-200
                              bg-white
                              hover:border-violet-300
                              hover:bg-violet-50/40
                            `
                        }
                      `}
                    >
                      <span
                        className="
                          mx-auto
                          block
                          h-7
                          w-7
                          rounded-full
                          border-2
                          border-white
                          shadow-[0_0_0_1px_#cbd5e1]
                        "
                        style={{
                          backgroundColor:
                            colorMap[product.color] || "#d1d5db",
                        }}
                      />

                      <span
                        className={`
                          mt-2
                          block
                          text-xs
                          font-bold

                          ${
                            isSelected
                              ? "text-violet-700"
                              : "text-slate-600"
                          }
                        `}
                      >
                        {product.color || "-"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* CONDITION */}
            <div className="mt-6">
              <span
                className="
                  mb-3
                  block
                  text-[11px]
                  font-extrabold
                  uppercase
                  tracking-[0.12em]
                  text-slate-400
                "
              >
                {text.condition}
              </span>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {conditionVariants.map((product) => {
                  const isSelected = selectedId === product.id;
                  const condition =
                    product.product_condition || "-";

                  return (
                    <button
                      type="button"
                      key={`condition-${product.id}`}
                      onClick={() => selectProduct(product)}
                      className={`
                        min-h-[62px]
                        rounded-xl
                        border
                        px-3
                        py-3
                        text-center
                        text-xs
                        font-extrabold
                        transition

                        ${
                          isSelected
                            ? `
                              border-emerald-500
                              bg-emerald-50
                              text-emerald-700
                              shadow-[0_0_0_3px_rgba(16,185,129,0.10)]
                            `
                            : `
                              border-slate-200
                              bg-white
                              text-slate-600
                              hover:border-emerald-300
                              hover:bg-emerald-50/50
                            `
                        }
                      `}
                    >
                      {condition}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.section>

        {/* =====================================================
            RIGHT — PRODUCT DETAILS
        ====================================================== */}
        <motion.section
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.08 }}
          className="
            flex
            flex-col
            rounded-[26px]
            border border-slate-200
            bg-white
            p-6
            shadow-[0_18px_50px_rgba(15,23,42,0.08)]
            sm:p-8
          "
        >
          {/* BRAND */}
          <span
            className="
              w-fit
              rounded-full
              bg-violet-100
              px-3
              py-1.5
              text-xs
              font-extrabold
              text-violet-700
            "
          >
            {mainProduct.brand || "Master Sol&Tech"}
          </span>

          {/* TITLE */}
          <h1
            className="
              mt-4
              text-3xl
              font-black
              tracking-tight
              text-slate-950
              sm:text-4xl
            "
          >
            {mainProduct.name}
          </h1>

          <p className="mt-2 text-sm font-medium text-slate-500">
            {mainProduct.brand} • {mainProduct.model}
          </p>

          {/* PRICE */}
          <div className="mt-7">
            <p className="text-3xl font-black text-green-600">
              €{Number(mainProduct.price || 0).toFixed(2)}
            </p>

            <div className="mt-3">
              {mainProduct.stock > 0 ? (
                <span
                  className="
                    inline-flex
                    items-center
                    gap-2
                    rounded-full
                    bg-green-50
                    px-3
                    py-2
                    text-sm
                    font-extrabold
                    text-green-700
                  "
                >
                  <span
                    className="
                      flex
                      h-5
                      w-5
                      items-center
                      justify-center
                      rounded-full
                      bg-green-500
                      text-xs
                      text-white
                    "
                  >
                    ✓
                  </span>

                  {text.inStock}
                </span>
              ) : (
                <span
                  className="
                    inline-flex
                    items-center
                    gap-2
                    rounded-full
                    bg-red-50
                    px-3
                    py-2
                    text-sm
                    font-extrabold
                    text-red-700
                  "
                >
                  ✕ {text.outStock}
                </span>
              )}
            </div>
          </div>

          <div className="my-6 h-px bg-slate-100" />

          {/* SPECIFICATIONS */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div
              className="
                flex
                min-h-[86px]
                items-center
                gap-4
                rounded-2xl
                border border-slate-200
                bg-slate-50/70
                p-4
              "
            >
              <div
                className="
                  flex
                  h-10
                  w-10
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-indigo-100
                  text-indigo-700
                "
              >
                <i className="fas fa-microchip" />
              </div>

              <div>
                <span className="text-xs font-bold text-slate-500">
                  {text.memory}
                </span>

                <p className="mt-1 font-extrabold text-slate-900">
                  {mainProduct.memory || "-"}
                </p>
              </div>
            </div>

            <div
              className="
                flex
                min-h-[86px]
                items-center
                gap-4
                rounded-2xl
                border border-slate-200
                bg-slate-50/70
                p-4
              "
            >
              <div
                className="
                  flex
                  h-10
                  w-10
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-indigo-100
                  text-indigo-700
                "
              >
                <i className="fas fa-battery-three-quarters" />
              </div>

              <div>
                <span className="text-xs font-bold text-slate-500">
                  {text.battery}
                </span>

                <p className="mt-1 font-extrabold text-slate-900">
                  {mainProduct.battery || "-"}
                </p>
              </div>
            </div>

            <div
              className="
                flex
                min-h-[86px]
                items-center
                gap-4
                rounded-2xl
                border border-slate-200
                bg-slate-50/70
                p-4
              "
            >
              <div
                className="
                  flex
                  h-10
                  w-10
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-indigo-100
                  text-indigo-700
                "
              >
                <i className="fas fa-palette" />
              </div>

              <div>
                <span className="text-xs font-bold text-slate-500">
                  {text.color}
                </span>

                <p className="mt-1 font-extrabold text-slate-900">
                  {mainProduct.color || "-"}
                </p>
              </div>
            </div>

            <div
              className="
                flex
                min-h-[86px]
                items-center
                gap-4
                rounded-2xl
                border border-slate-200
                bg-slate-50/70
                p-4
              "
            >
              <div
                className="
                  flex
                  h-10
                  w-10
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-indigo-100
                  text-indigo-700
                "
              >
                <i className="fas fa-shield-alt" />
              </div>

              <div>
                <span className="text-xs font-bold text-slate-500">
                  {text.condition}
                </span>

                <p className="mt-1 font-extrabold text-slate-900">
                  {mainProduct.product_condition || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* DESCRIPTION */}
          <div
            className="
              mt-5
              rounded-2xl
              border border-slate-200
              bg-slate-50/70
              p-4
            "
          >
            <span
              className="
                text-[11px]
                font-extrabold
                uppercase
                tracking-[0.1em]
                text-violet-700
              "
            >
              Përshkrimi
            </span>

            <p className="mt-2 text-sm leading-6 text-slate-600">
              {mainProduct.description || text.noDesc}
            </p>
          </div>

          {/* BUTTONS */}
          <div className="mt-auto grid grid-cols-1 gap-3 pt-7 sm:grid-cols-2">
            <button
              type="button"
              onClick={handleWhatsApp}
              className="
                flex
                min-h-13
                items-center
                justify-center
                gap-3
                rounded-xl
                bg-green-600
                py-3
                text-sm
                font-extrabold
                text-white
                shadow-[0_12px_25px_rgba(22,163,74,0.25)]
                transition
                hover:-translate-y-0.5
                hover:bg-green-700
              "
            >
              <i className="fab fa-whatsapp text-lg" />
              {text.whatsapp}
            </button>

            <button
              type="button"
              onClick={() => setMode("reserve")}
              className="
                flex
                min-h-13
                items-center
                justify-center
                gap-3
                rounded-xl
                bg-gradient-to-r
                from-violet-600
                to-fuchsia-600
                py-3
                text-sm
                font-extrabold
                text-white
                shadow-[0_12px_25px_rgba(124,58,237,0.25)]
                transition
                hover:-translate-y-0.5
                hover:from-violet-700
                hover:to-fuchsia-700
              "
            >
              <i className="fas fa-calendar-check" />
              {text.reserve}
            </button>
          </div>
        </motion.section>
      </div>
    </div>

    {/* =====================================================
        RESERVATION MODAL
    ====================================================== */}
    {mode === "reserve" && (
      <div
        className="
          fixed
          inset-0
          z-50
          flex
          items-center
          justify-center
          bg-slate-950/60
          p-4
          backdrop-blur-sm
        "
        onClick={() => setMode(null)}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          onClick={(event) => event.stopPropagation()}
          className="
            relative
            w-full
            max-w-md
            rounded-3xl
            border border-slate-200
            bg-white
            p-6
            shadow-[0_30px_80px_rgba(15,23,42,0.30)]
          "
        >
          <button
            type="button"
            onClick={() => setMode(null)}
            className="
              absolute
              right-5
              top-5
              flex
              h-9
              w-9
              items-center
              justify-center
              rounded-full
              bg-slate-100
              text-slate-600
              transition
              hover:bg-red-50
              hover:text-red-600
            "
          >
            ✕
          </button>

          <h2 className="pr-12 text-xl font-black text-slate-900">
            {text.reserveTitle}
          </h2>

          <p className="mt-2 text-sm text-slate-500">
            {mainProduct.name}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              name="name"
              placeholder={text.name}
              value={form.name}
              onChange={handleChange}
              className="
                h-11
                rounded-xl
                border border-slate-200
                bg-slate-50
                px-3
                text-sm
                outline-none
                transition
                focus:border-violet-500
                focus:bg-white
                focus:ring-4
                focus:ring-violet-100
              "
            />

            <input
              name="surname"
              placeholder={text.surname}
              value={form.surname}
              onChange={handleChange}
              className="
                h-11
                rounded-xl
                border border-slate-200
                bg-slate-50
                px-3
                text-sm
                outline-none
                transition
                focus:border-violet-500
                focus:bg-white
                focus:ring-4
                focus:ring-violet-100
              "
            />
          </div>

          <input
            name="mobile"
            placeholder={text.phone}
            value={form.mobile}
            onChange={handleChange}
            className="
              mt-3
              h-11
              w-full
              rounded-xl
              border border-slate-200
              bg-slate-50
              px-3
              text-sm
              outline-none
              transition
              focus:border-violet-500
              focus:bg-white
              focus:ring-4
              focus:ring-violet-100
            "
          />

          <textarea
            name="address"
            placeholder={text.address}
            value={form.address}
            onChange={handleChange}
            rows="4"
            className="
              mt-3
              w-full
              resize-none
              rounded-xl
              border border-slate-200
              bg-slate-50
              p-3
              text-sm
              outline-none
              transition
              focus:border-violet-500
              focus:bg-white
              focus:ring-4
              focus:ring-violet-100
            "
          />

          <button
            type="button"
            disabled={loading}
            onClick={handleReserve}
            className="
              mt-4
              min-h-12
              w-full
              rounded-xl
              bg-gradient-to-r
              from-violet-600
              to-fuchsia-600
              text-sm
              font-extrabold
              text-white
              shadow-lg
              shadow-violet-200
              transition
              hover:from-violet-700
              hover:to-fuchsia-700
              disabled:cursor-not-allowed
              disabled:opacity-60
            "
          >
            {loading ? text.loading : text.confirm}
          </button>

          <button
            type="button"
            onClick={() => setMode(null)}
            className="
              mt-3
              w-full
              py-2
              text-sm
              font-bold
              text-violet-700
              transition
              hover:text-violet-900
            "
          >
            {text.close}
          </button>
        </motion.div>
      </div>
    )}
  </main>
);
};

export default ProductDetails;